import { create } from 'zustand'

import type { DownloadProgress } from '@/helpers/downloadProgress'

/**
 * How far each file's bytes have come, by file id.
 *
 * The download runs inside a query function, which can say nothing to the
 * components waiting on it — and the component that started it is not always
 * the one showing the wait, since the query is shared. So the fetch reports
 * here and whoever is waiting reads it.
 *
 * A record is taken back to nothing as its download starts, so a second
 * download of the same file does not open on the last one's full bar, and is
 * left standing at the whole once it is done: the moment the bytes land is not
 * one for the bar to vanish in, and the waiter has the bytes by then.
 */
interface FileDownloadStore {
  byFile: Record<string, DownloadProgress>
  report: (fileId: string, progress: DownloadProgress) => void
  clear: (fileId: string) => void
}

export const useFileDownloadStore = create<FileDownloadStore>()((set) => ({
  byFile: {},
  report: (fileId, progress) =>
    set((state) => ({ byFile: { ...state.byFile, [fileId]: progress } })),
  clear: (fileId) =>
    set((state) => {
      const { [fileId]: _, ...rest } = state.byFile
      return { byFile: rest }
    }),
}))

export const useFileDownloadProgress = (
  fileId: string,
): DownloadProgress | null =>
  useFileDownloadStore((state) => state.byFile[fileId] ?? null)
